import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  PlayIcon, 
  PauseIcon,
  HeartIcon, 
  ShareIcon, 
  ChatBubbleLeftIcon,
  ArrowLeftIcon,
  SpeakerWaveIcon,
  BookmarkIcon,
  EllipsisHorizontalIcon,
  CubeTransparentIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  ChartBarIcon,
  UsersIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { useWorks } from '../contexts/WorksContext';
import { useLanguage } from '../contexts/LanguageContext';

interface Comment {
  id: number;
  user: {
    id: number;
    name: string;
    avatar: string;
  };
  content: string;
  timestamp: string;
  likes: number;
  liked: boolean;
}

interface WorkDetailData {
  id: number;
  title: string;
  artist: {
    id: number;
    name: string;
    avatar: string;
    verified: boolean;
  };
  collaborator?: {
    id: number;
    name: string;
    avatar: string;
  };
  coverUrl: string;
  audioUrl?: string;
  duration: string;
  genre: string;
  description: string;
  plays: number;
  likes: number;
  comments: number;
  liked: boolean;
  bookmarked: boolean;
  releaseDate: string;
  tags: string[];
  copyrightInfo: {
    composer: string;
    lyricist: string;
    producer: string;
    label: string;
  };
  relatedWorks: Array<{
    id: number;
    title: string;
    artist: string;
    coverUrl: string;
    duration: string;
    plays: number;
  }>;
  commentsList: Comment[];
  isPioneer?: boolean;
  price?: number;
  currency?: string;
  type?: 'demo' | 'full-version';
  pioneersCount?: number;
  nextPurchaseReward?: number;
}

interface RegularLicensePlan {
  price: number;
  name: string;
  features: string[];
  color: string;
  icon: string;
}

interface PioneerLicensePlan {
  price: number;
  name: string;
  features: string[];
  color: string;
  icon: string;
  rewardRate: number;
  rewardPerPurchase: number;
}

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  work: WorkDetailData;
  language: string;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({ isOpen, onClose, work, language }) => {
  const [selectedLicense, setSelectedLicense] = useState<'basic' | 'standard' | 'premium'>('basic');
  const [quantity, setQuantity] = useState(1);

  if (!isOpen) return null;

  // 根据作品类型判断是否显示先行者功能
  const showPioneerFeatures = work?.isPioneer && work?.type === 'full-version';

  // 普通版权许可计划（非先行者作品）
  const regularLicensePlans: Record<string, RegularLicensePlan> = {
    basic: {
      price: 88,
      name: language === 'zh' ? '基础许可' : 'Basic License',
      features: [
        language === 'zh' ? '数字下载使用权' : 'Digital Download Rights',
        language === 'zh' ? '个人项目使用' : 'Personal Project Use',
        language === 'zh' ? '高品质音频文件' : 'High Quality Audio File',
        language === 'zh' ? '永久使用权' : 'Lifetime Usage Rights'
      ],
      color: 'blue',
      icon: '🎵'
    },
    standard: {
      price: 188,
      name: language === 'zh' ? '标准许可' : 'Standard License',
      features: [
        language === 'zh' ? '包含基础许可所有内容' : 'All Basic License Features',
        language === 'zh' ? '商业复制使用权' : 'Commercial Reproduction Rights',
        language === 'zh' ? '音频编辑授权' : 'Audio Editing License',
        language === 'zh' ? '社交媒体发布权' : 'Social Media Publishing Rights',
        language === 'zh' ? '背景音乐使用' : 'Background Music Usage'
      ],
      color: 'purple',
      icon: '🎼'
    },
    premium: {
      price: 388,
      name: language === 'zh' ? '高级许可' : 'Premium License',
      features: [
        language === 'zh' ? '包含标准许可所有内容' : 'All Standard License Features',
        language === 'zh' ? '公开表演授权' : 'Public Performance License',
        language === 'zh' ? '广播媒体使用权' : 'Broadcasting Rights',
        language === 'zh' ? '商业演出授权' : 'Commercial Performance License',
        language === 'zh' ? '无限次使用许可' : 'Unlimited Usage License',
        language === 'zh' ? '专属客服支持' : 'Dedicated Support'
      ],
      color: 'gold',
      icon: '👑'
    }
  };

  // 先行者版权许可计划（带返利功能）
  const pioneerLicensePlans: Record<string, PioneerLicensePlan> = {
    basic: {
      price: 88,
      name: language === 'zh' ? '基础先行者' : 'Basic Pioneer',
      features: [
        language === 'zh' ? '数字下载使用权' : 'Digital Download Rights',
        language === 'zh' ? '个人项目使用' : 'Personal Project Use',
        language === 'zh' ? '高品质音频文件' : 'High Quality Audio File',
        language === 'zh' ? '永久使用权' : 'Lifetime Usage Rights',
        language === 'zh' ? '先行者身份认证' : 'Pioneer Status Badge',
        language === 'zh' ? '后续购买者返利' : 'Referral Rewards from Future Buyers'
      ],
      color: 'blue',
      icon: '⭐',
      rewardRate: 0.15,
      rewardPerPurchase: work?.nextPurchaseReward || 13
    },
    standard: {
      price: 188,
      name: language === 'zh' ? '标准先行者' : 'Standard Pioneer',
      features: [
        language === 'zh' ? '包含基础先行者所有权益' : 'All Basic Pioneer Features',
        language === 'zh' ? '商业复制使用权' : 'Commercial Reproduction Rights',
        language === 'zh' ? '音频编辑授权' : 'Audio Editing License',
        language === 'zh' ? '社交媒体发布权' : 'Social Media Publishing Rights',
        language === 'zh' ? '优先新作品体验' : 'Priority New Release Access',
        language === 'zh' ? '更高返利比例' : 'Higher Referral Rate'
      ],
      color: 'purple',
      icon: '👑',
      rewardRate: 0.20,
      rewardPerPurchase: work?.nextPurchaseReward || 28
    },
    premium: {
      price: 388,
      name: language === 'zh' ? '高级先行者' : 'Premium Pioneer',
      features: [
        language === 'zh' ? '包含标准先行者所有权益' : 'All Standard Pioneer Features',
        language === 'zh' ? '公开表演授权' : 'Public Performance License',
        language === 'zh' ? '广播媒体使用权' : 'Broadcasting Rights',
        language === 'zh' ? '商业演出授权' : 'Commercial Performance License',
        language === 'zh' ? '无限次使用许可' : 'Unlimited Usage License',
        language === 'zh' ? '专属客服支持' : 'Dedicated Support',
        language === 'zh' ? '最高返利比例' : 'Maximum Referral Rate'
      ],
      color: 'gold',
      icon: '💎',
      rewardRate: 0.25,
      rewardPerPurchase: work?.nextPurchaseReward || 58
    }
  };

  const currentPlans = showPioneerFeatures ? pioneerLicensePlans : regularLicensePlans;
  const currentPlan = currentPlans[selectedLicense];
  const unitPrice = currentPlan.price;
  const totalPrice = unitPrice * quantity;

  const getColorClasses = (color: string, isSelected: boolean) => {
    const colorMap = {
      blue: isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300',
      purple: isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300',
      gold: isSelected ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-yellow-300'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  // 类型守卫函数
  const isPioneerPlan = (plan: RegularLicensePlan | PioneerLicensePlan): plan is PioneerLicensePlan => {
    return 'rewardPerPurchase' in plan;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className={`relative text-white px-6 py-4 flex items-center justify-between flex-shrink-0 ${
          showPioneerFeatures 
            ? 'bg-gradient-to-r from-purple-600 to-blue-600' 
            : 'bg-gradient-to-r from-blue-600 to-gray-600'
        }`}>
          <div className="flex items-center gap-3">
            <div className="text-2xl">{showPioneerFeatures ? '⭐' : '🎵'}</div>
            <div>
              <h2 className="font-bold text-lg">
                {showPioneerFeatures 
                  ? (language === 'zh' ? '先行者版权许可' : 'Pioneer Copyright License')
                  : (language === 'zh' ? '版权许可购买' : 'Copyright License Purchase')
                }
              </h2>
              <p className={`text-sm ${showPioneerFeatures ? 'text-purple-100' : 'text-blue-100'}`}>
                {work?.title}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white hover:bg-opacity-20 rounded">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            
            {/* Work Info */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-lg p-4 sticky top-0">
                <img src={work?.coverUrl} alt={work?.title} className="w-full h-32 rounded object-cover mb-3" />
                <h3 className="font-bold text-sm mb-1">{work?.title}</h3>
                <p className="text-xs text-gray-600 mb-2">{work?.artist?.name || ''}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">{work?.genre}</span>
                  {work?.duration && (
                    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">{work?.duration}</span>
                  )}
                  {work?.type === 'demo' && (
                    <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">Demo</span>
                  )}
                </div>
                
                {showPioneerFeatures ? (
                  <div className="bg-gradient-to-r from-purple-100 to-blue-100 p-3 rounded text-xs text-center">
                    <StarIcon className="w-5 h-5 mx-auto mb-2 text-purple-600" />
                    <div className="font-bold text-purple-800">{language === 'zh' ? '先行者作品' : 'Pioneer Work'}</div>
                    <div className="text-purple-600 mt-1">{language === 'zh' ? '早买早受益' : 'Early Buyers Benefit More'}</div>
                    {work?.pioneersCount && (
                      <div className="text-xs text-gray-600 mt-2">
                        {language === 'zh' ? `已有 ${work.pioneersCount} 位先行者` : `${work.pioneersCount} pioneers already`}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-100 p-3 rounded text-xs text-center">
                    <div className="font-bold text-gray-800">{language === 'zh' ? '标准作品' : 'Standard Work'}</div>
                    <div className="text-gray-600 mt-1">{language === 'zh' ? '版权许可使用' : 'Copyright License Usage'}</div>
                  </div>
                )}
              </div>
            </div>

            {/* License Plans */}
            <div className="lg:col-span-3">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                {showPioneerFeatures ? <StarIcon className="w-6 h-6 text-purple-600" /> : <CurrencyDollarIcon className="w-6 h-6 text-blue-600" />}
                {showPioneerFeatures 
                  ? (language === 'zh' ? '选择先行者等级' : 'Choose Pioneer Level')
                  : (language === 'zh' ? '选择许可计划' : 'Choose License Plan')
                }
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {Object.entries(currentPlans).map(([key, plan]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedLicense(key as any)}
                    className={`p-6 rounded-xl border-2 transition-all text-left h-full ${
                      getColorClasses(plan.color, selectedLicense === key)
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="text-2xl">{plan.icon}</div>
                      <div>
                        <h4 className="font-bold text-lg">{plan.name}</h4>
                        <div className="text-2xl font-bold text-gray-900">
                          {work?.currency || 'HK$'} {plan.price}
                        </div>
                      </div>
                    </div>
                    
                    {/* Pioneer Reward Preview */}
                    {showPioneerFeatures && isPioneerPlan(plan) && (
                      <div className="bg-white bg-opacity-60 rounded-lg p-3 mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs text-gray-600">
                            {language === 'zh' ? '每次后续购买返利' : 'Reward per Future Purchase'}
                          </span>
                          <span className="font-bold text-green-600">
                            {`+${work?.currency || 'HK$'} ${plan.rewardPerPurchase}`}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {language === 'zh' ? '有人购买相同等级即可获得' : 'Earned when someone buys same level'}
                        </div>
                      </div>
                    )}
                    
                    <ul className="space-y-2 text-sm">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-green-500 mt-0.5">✓</span>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>

              {/* Quantity Selection */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">
                      {language === 'zh' ? '购买数量' : 'Purchase Quantity'}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {showPioneerFeatures 
                        ? (language === 'zh' ? '每份许可包含使用权和先行者权益' : 'Each license includes usage rights and pioneer benefits')
                        : (language === 'zh' ? '每个许可证可用于一个项目' : 'Each license valid for one project')
                      }
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 bg-white border border-gray-300 rounded-lg font-bold hover:bg-gray-100 transition-colors"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-bold text-xl">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 bg-white border border-gray-300 rounded-lg font-bold hover:bg-gray-100 transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Purchase Summary */}
              <div className={`rounded-lg p-6 border ${
                showPioneerFeatures 
                  ? 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200' 
                  : 'bg-gradient-to-r from-blue-50 to-gray-50 border-blue-200'
              }`}>
                <h4 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  {showPioneerFeatures ? <StarIcon className="w-5 h-5 text-purple-600" /> : <CurrencyDollarIcon className="w-5 h-5 text-blue-600" />}
                  {showPioneerFeatures 
                    ? (language === 'zh' ? '先行者权益摘要' : 'Pioneer Benefits Summary')
                    : (language === 'zh' ? '购买摘要' : 'Purchase Summary')
                  }
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* License Rights */}
                  <div className="bg-white rounded-lg p-4">
                    <h5 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2">
                      🎵 {language === 'zh' ? '版权许可' : 'Copyright License'}
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{currentPlan.name} × {quantity}</span>
                        <span className="font-medium">{work?.currency || 'HK$'} {totalPrice.toLocaleString()}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {language === 'zh' ? '立即获得完整使用权限' : 'Immediate full usage rights'}
                      </div>
                    </div>
                  </div>

                  {/* Pioneer Benefits or Regular Benefits */}
                  <div className="bg-white rounded-lg p-4">
                    <h5 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2">
                      {showPioneerFeatures ? '⭐' : '📄'} {showPioneerFeatures 
                        ? (language === 'zh' ? '先行者返利' : 'Pioneer Rewards')
                        : (language === 'zh' ? '许可权益' : 'License Benefits')
                      }
                    </h5>
                    {showPioneerFeatures && isPioneerPlan(currentPlan) ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between text-green-600">
                          <span>{language === 'zh' ? '单次返利' : 'Per Purchase Reward'}</span>
                          <span className="font-medium">
                            {`+${work?.currency || 'HK$'} ${currentPlan.rewardPerPurchase * quantity}`}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {language === 'zh' ? '每有新用户购买相同等级即可获得' : 'Earned when new users buy same level'}
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">
                        {language === 'zh' ? '标准版权许可使用权限' : 'Standard copyright license usage rights'}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg">{language === 'zh' ? '总支付金额' : 'Total Payment'}</span>
                    <span className={`font-bold text-2xl ${showPioneerFeatures ? 'text-purple-600' : 'text-blue-600'}`}>
                      {work?.currency || 'HK$'} {totalPrice.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Benefits Summary */}
                <div className="bg-white rounded-lg p-4">
                  <h5 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2">
                    {showPioneerFeatures ? '🎯' : '📋'} {language === 'zh' ? '您将获得' : 'You Will Get'}
                  </h5>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-500">📄</span>
                      <span>{language === 'zh' ? '数字许可证书' : 'Digital License Certificate'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-500">🔒</span>
                      <span>{language === 'zh' ? '法律权益保护' : 'Legal Protection'}</span>
                    </div>
                    {showPioneerFeatures ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-purple-500">⭐</span>
                          <span>{language === 'zh' ? '先行者身份认证' : 'Pioneer Status Badge'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-500">💰</span>
                          <span>{language === 'zh' ? '后续购买返利' : 'Future Purchase Rewards'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-blue-500">👥</span>
                          <span>{language === 'zh' ? '专属社区权限' : 'Exclusive Community'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-orange-500">🚀</span>
                          <span>{language === 'zh' ? '新作品优先体验' : 'Priority New Releases'}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-blue-500">📧</span>
                          <span>{language === 'zh' ? '邮件确认' : 'Email Confirmation'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-green-500">⏰</span>
                          <span>{language === 'zh' ? '即时激活' : 'Instant Activation'}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 p-6 flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
          >
            {language === 'zh' ? '取消' : 'Cancel'}
          </button>
          <button
            onClick={() => {
              const purchaseType = showPioneerFeatures ? (language === 'zh' ? '先行者' : 'Pioneer') : (language === 'zh' ? '标准' : 'Standard');
              const rewardText = showPioneerFeatures && isPioneerPlan(currentPlan) 
                ? `\n${language === 'zh' ? '单次返利' : 'Per Purchase Reward'}: +${work?.currency || 'HK$'} ${currentPlan.rewardPerPurchase * quantity}` 
                : '';
              
              alert(
                `${purchaseType}${language === 'zh' ? '购买确认' : ' Purchase Confirmation'}\n\n` +
                `${language === 'zh' ? '作品' : 'Work'}: ${work?.title}\n` +
                `${language === 'zh' ? '等级' : 'Level'}: ${currentPlan.name}\n` +
                `${language === 'zh' ? '数量' : 'Quantity'}: ${quantity}\n` +
                `${language === 'zh' ? '总价' : 'Total'}: ${work?.currency || 'HK$'} ${totalPrice.toLocaleString()}${rewardText}\n\n` +
                `${language === 'zh' ? '购买成功！功能开发中，敬请期待！' : 'Purchase successful! Feature coming soon!'}`
              );
              onClose();
            }}
            className={`flex-2 py-3 px-8 text-white rounded-lg transition-all font-medium flex items-center justify-center gap-2 ${
              showPioneerFeatures
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                : 'bg-gradient-to-r from-blue-600 to-gray-600 hover:from-blue-700 hover:to-gray-700'
            }`}
          >
            {showPioneerFeatures ? <StarIcon className="w-5 h-5" /> : <CurrencyDollarIcon className="w-5 h-5" />}
            {showPioneerFeatures 
              ? `${language === 'zh' ? '成为先行者' : 'Become Pioneer'} - ${work?.currency || 'HK$'} ${totalPrice.toLocaleString()}`
              : `${language === 'zh' ? '立即购买' : 'Buy Now'} - ${work?.currency || 'HK$'} ${totalPrice.toLocaleString()}`
            }
          </button>
        </div>
      </div>
    </div>
  );
};

const WorkDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { publishedWorks } = useWorks();
  const [work, setWork] = useState<WorkDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [newComment, setNewComment] = useState('');
  const [purchaseModal, setPurchaseModal] = useState(false);
  const { t, language } = useLanguage();

  useEffect(() => {
    const fetchWorkDetail = async () => {
      try {
        // 根据ID生成不同类型的作品数据
        const workId = parseInt(id || '1');
        const isPioneerWork = workId % 3 === 1 || workId % 3 === 2; // 2/3的作品是先行者
        const isDemoWork = workId % 4 === 3; // 1/4的作品是demo
        
        const mockData: WorkDetailData = {
          id: workId,
          title: isDemoWork 
            ? (language === 'zh' ? '银河漫游 (Demo)' : 'Galaxy Travel (Demo)')
            : (language === 'zh' ? '银河漫游' : 'Galaxy Travel'),
          artist: {
            id: 1,
            name: language === 'zh' ? '陈小薇' : 'Chen Xiaowei',
            avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80',
            verified: true
          },
          collaborator: {
            id: 2,
            name: language === 'zh' ? '天宇音乐' : 'Tianyu Music',
            avatar: 'https://images.unsplash.com/photo-1494790108755-2616c3584643?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
          },
          coverUrl: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          duration: isDemoWork ? '1:30 / 2:15' : '2:15 / 4:30',
          genre: language === 'zh' ? '流行' : 'Pop',
          description: isDemoWork 
            ? (language === 'zh' ? '这是一首充满未来感的电子流行音乐作品的Demo版本，展现了基础的旋律和编曲结构，让听众感受到完整版本的潜力。' : 'This is a demo version of a futuristic electronic pop music work, showcasing basic melody and arrangement structure, giving listeners a taste of the full version\'s potential.')
            : (language === 'zh' ? '这是一首充满未来感的电子流行音乐作品，融合了深邃的宇宙主题和现代音乐元素。创作灵感来源于对太空探索的向往，旋律优美动听，节拍富有层次感，为听众带来一场音乐上的银河之旅。' : 'This is a futuristic electronic pop music work that combines profound cosmic themes with modern musical elements. The creative inspiration comes from the yearning for space exploration, with beautiful melodies and rhythmic layers, bringing listeners a musical journey through the galaxy.'),
          plays: 28000,
          likes: 2800,
          comments: 128,
          liked: false,
          bookmarked: false,
          releaseDate: '2023-08-15',
          tags: isDemoWork 
            ? [language === 'zh' ? '流行' : 'Pop', language === 'zh' ? '电子' : 'Electronic', 'Demo', language === 'zh' ? '原创' : 'Original']
            : [language === 'zh' ? '流行' : 'Pop', language === 'zh' ? '电子' : 'Electronic', language === 'zh' ? '治愈' : 'Healing', language === 'zh' ? '原创' : 'Original'],
          copyrightInfo: {
            composer: language === 'zh' ? '陈小薇' : 'Chen Xiaowei',
            lyricist: language === 'zh' ? '陈小薇' : 'Chen Xiaowei',
            producer: language === 'zh' ? '天宇工作室' : 'Tianyu Studio',
            label: language === 'zh' ? 'iBOM唱片' : 'iBOM Records'
          },
          relatedWorks: [
            {
              id: 2,
              title: language === 'zh' ? '幻影' : 'Mirage',
              artist: language === 'zh' ? '袁语汐' : 'Yuan Yuxi',
              coverUrl: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
              duration: '03:45',
              plays: 15400
            },
            {
              id: 3,
              title: language === 'zh' ? '夜空许愿 (Demo)' : 'Night Sky Wish (Demo)',
              artist: language === 'zh' ? '杨语明' : 'Yang Yuming',
              coverUrl: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
              duration: '02:12',
              plays: 8100
            },
            {
              id: 4,
              title: language === 'zh' ? '春天的故事' : 'Spring Story',
              artist: language === 'zh' ? '小雨音乐' : 'Xiaoyu Music',
              coverUrl: 'https://images.unsplash.com/photo-1511735111819-9a3f7709049c?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
              duration: '03:28',
              plays: 18600
            }
          ],
          commentsList: [
            {
              id: 1,
              user: {
                id: 1,
                name: language === 'zh' ? '张美琪' : 'Zhang Meiqi',
                avatar: 'https://images.unsplash.com/photo-1494790108755-2616c3584643?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
              },
              content: isDemoWork
                ? (language === 'zh' ? '这个Demo很有潜力！期待完整版，相信会更精彩。' : 'This demo has great potential! Looking forward to the full version, it will be amazing.')
                : (language === 'zh' ? '这首歌真的太棒了！旋律优美，让人仿佛置身于浩瀚的宇宙中。' : 'This song is absolutely amazing! The melody is beautiful, making me feel like I\'m in the vast universe.'),
              timestamp: '3' + (language === 'zh' ? '小时前' : ' hours ago'),
              likes: 34,
              liked: false
            },
            {
              id: 2,
              user: {
                id: 2,
                name: language === 'zh' ? '王志强' : 'Wang Zhiqiang',
                avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
              },
              content: isPioneerWork && !isDemoWork
                ? (language === 'zh' ? '已经成为先行者了！希望能从后续的购买中获得一些返利。支持原创音乐！' : 'Already became a pioneer! Hope to get some rewards from future purchases. Supporting original music!')
                : (language === 'zh' ? '制作水准很高！期待更多作品！支持原创音乐。' : 'High production quality, looking forward to more works! Supporting original music.'),
              timestamp: '5' + (language === 'zh' ? '小时前' : ' hours ago'),
              likes: 28,
              liked: true
            }
          ],
          isPioneer: isPioneerWork,
          price: isDemoWork ? 0 : 88,
          currency: 'HK$',
          type: isDemoWork ? 'demo' : 'full-version',
          pioneersCount: isPioneerWork ? Math.floor(Math.random() * 50) + 10 : undefined,
          nextPurchaseReward: isPioneerWork ? Math.floor(Math.random() * 20) + 10 : undefined
        };
        
        setWork(mockData);
      } catch (error) {
        console.error('Failed to fetch work detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkDetail();
  }, [id, language]);

  const handleLike = () => {
    if (work) {
      const newLiked = !work.liked;
      setWork({
        ...work,
        liked: newLiked,
        likes: newLiked ? work.likes + 1 : work.likes - 1
      });
    }
  };

  const handleBookmark = () => {
    if (work) {
      setWork({
        ...work,
        bookmarked: !work.bookmarked
      });
    }
  };

  const handleCommentLike = (commentId: number) => {
    if (work) {
      const updatedComments = work.commentsList.map(comment => {
        if (comment.id === commentId) {
          return {
            ...comment,
            liked: !comment.liked,
            likes: comment.liked ? comment.likes - 1 : comment.likes + 1
          };
        }
        return comment;
      });
      setWork({ ...work, commentsList: updatedComments });
    }
  };

  const handleSubmitComment = () => {
    if (work && newComment.trim()) {
      const comment: Comment = {
        id: Date.now(),
        user: {
          id: 999,
          name: language === 'zh' ? '我' : 'Me',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
        },
        content: newComment,
        timestamp: language === 'zh' ? '刚刚' : 'Now',
        likes: 0,
        liked: false
      };
      setWork({
        ...work,
        commentsList: [comment, ...work.commentsList],
        comments: work.comments + 1
      });
      setNewComment('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!work) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{language === 'zh' ? '作品不存在' : 'Work does not exist'}</h2>
          <button
            onClick={() => navigate('/works')}
            className="text-blue-600 hover:text-blue-800"
          >
            {language === 'zh' ? '返回作品列表' : 'Back to works list'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={() => navigate('/works')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900">{language === 'zh' ? '音乐作品' : 'Music Works'}</h1>
            {work.isPioneer && work.type === 'full-version' && (
              <div className="ml-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                <StarIcon className="w-4 h-4" />
                {language === 'zh' ? '先行者作品' : 'Pioneer Work'}
              </div>
            )}
            {work.type === 'demo' && (
              <div className="ml-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                Demo
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Player Section */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="relative">
                <img 
                  src={work.coverUrl} 
                  alt={work.title}
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                
                {/* Badges Container */}
                <div className="absolute top-6 right-6 flex flex-col gap-2">
                  {work.isPioneer && work.type === 'full-version' && (
                    <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 py-2 rounded-full text-sm font-medium flex items-center gap-1 shadow-lg">
                      <StarIcon className="w-4 h-4" />
                      {language === 'zh' ? '先行者项目' : 'Pioneer Project'}
                    </span>
                  )}
                  
                  <span className={`px-3 py-2 rounded-full text-sm font-medium shadow-lg ${
                    work.type === 'demo' 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-green-500 text-white'
                  }`}>
                    {work.type === 'demo' ? 'Demo' : (language === 'zh' ? '完整版' : 'Full Version')}
                  </span>
                </div>
                
                {/* Play Controls Overlay */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <button 
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-4 transition-all shadow-lg"
                    >
                      {isPlaying ? (
                        <PauseIcon className="h-8 w-8 text-gray-900" />
                      ) : (
                        <PlayIcon className="h-8 w-8 text-gray-900" />
                      )}
                    </button>
                    
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-white mb-1">{work.title}</h2>
                      <div className="flex items-center space-x-2 text-white">
                        <img 
                          src={work.artist.avatar}
                          alt={work.artist.name}
                          className="w-6 h-6 rounded-full"
                        />
                        <span className="font-medium">{work.artist.name}</span>
                        {work.artist.verified && (
                          <div className="bg-blue-600 text-white px-2 py-0.5 rounded-full text-xs">
                            {language === 'zh' ? '认证' : 'Verified'}
                          </div>
                        )}
                        {work.collaborator && (
                          <>
                            <span className="text-gray-300">x</span>
                            <img 
                              src={work.collaborator.avatar}
                              alt={work.collaborator.name}
                              className="w-6 h-6 rounded-full"
                            />
                            <span className="font-medium">{work.collaborator.name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mb-2">
                    <div className="bg-white bg-opacity-30 rounded-full h-1">
                      <div 
                        className="bg-white rounded-full h-1 transition-all duration-1000"
                        style={{ width: `${(currentTime / 270) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-white text-sm">
                    <span>{work.duration}</span>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <SpeakerWaveIcon className="h-4 w-4" />
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={volume}
                          onChange={(e) => setVolume(parseFloat(e.target.value))}
                          className="w-20 h-1 bg-white bg-opacity-30 rounded-full appearance-none cursor-pointer"
                        />
                      </div>
                      <button className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors">
                        <EllipsisHorizontalIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Bar */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <button 
                    onClick={handleLike}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-colors ${
                      work.liked 
                        ? 'bg-red-50 text-red-600' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {work.liked ? (
                      <HeartIconSolid className="h-5 w-5" />
                    ) : (
                      <HeartIcon className="h-5 w-5" />
                    )}
                    <span>{work.likes}{language === 'zh' ? '赞' : ' likes'}</span>
                  </button>
                  
                  <button className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors">
                    <ChatBubbleLeftIcon className="h-5 w-5" />
                    <span>{work.comments} {language === 'zh' ? '评论' : 'comments'}</span>
                  </button>
                  
                  <button className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors">
                    <ShareIcon className="h-5 w-5" />
                    <span>{language === 'zh' ? '分享' : 'Share'}</span>
                  </button>

                  {/* Purchase/Preview Button */}
                  {work.type === 'demo' ? (
                    <button 
                      onClick={() => alert(language === 'zh' ? 'Demo试听功能开发中' : 'Demo preview feature coming soon')}
                      className="flex items-center space-x-2 px-6 py-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all font-medium"
                    >
                      <PlayIcon className="h-5 w-5" />
                      <span>{language === 'zh' ? '免费试听' : 'Free Preview'}</span>
                    </button>
                  ) : (
                    <button 
                      onClick={() => setPurchaseModal(true)}
                      className={`flex items-center space-x-2 px-6 py-2 rounded-full transition-all font-medium ${
                        work.isPioneer
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {work.isPioneer ? <StarIcon className="h-5 w-5" /> : <CurrencyDollarIcon className="h-5 w-5" />}
                      <span>
                        {work.isPioneer 
                          ? (language === 'zh' ? '成为先行者' : 'Become Pioneer')
                          : (language === 'zh' ? '购买许可' : 'Buy License')
                        }
                      </span>
                    </button>
                  )}
                </div>
                
                <button 
                  onClick={handleBookmark}
                  className={`p-2 rounded-full transition-colors ${
                    work.bookmarked 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <BookmarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Pioneer Benefits (Only for pioneer full-version works) */}
            {work.isPioneer && work.type === 'full-version' && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl shadow-lg p-6 border border-purple-200">
                <div className="flex items-center gap-3 mb-4">
                  <StarIcon className="w-6 h-6 text-purple-600" />
                  <h3 className="text-lg font-bold text-gray-900">{language === 'zh' ? '先行者特权' : 'Pioneer Benefits'}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="bg-white bg-opacity-60 p-4 rounded-lg">
                    <div className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                      <BoltIcon className="w-4 h-4" />
                      {language === 'zh' ? '版权许可' : 'Copyright License'}
                    </div>
                    <div className="text-gray-700">{language === 'zh' ? '获得完整的音乐使用权限，支持商业用途' : 'Full music usage rights including commercial use'}</div>
                  </div>
                  <div className="bg-white bg-opacity-60 p-4 rounded-lg">
                    <div className="font-bold text-purple-800 mb-2 flex items-center gap-2">
                      <UsersIcon className="w-4 h-4" />
                      {language === 'zh' ? '返利收益' : 'Referral Rewards'}
                    </div>
                    <div className="text-gray-700">{language === 'zh' ? '每有新用户购买相同等级即可获得返利' : 'Earn rewards when new users buy same level'}</div>
                  </div>
                  <div className="bg-white bg-opacity-60 p-4 rounded-lg">
                    <div className="font-bold text-green-800 mb-2 flex items-center gap-2">
                      <StarIcon className="w-4 h-4" />
                      {language === 'zh' ? '专属权益' : 'Exclusive Benefits'}
                    </div>
                    <div className="text-gray-700">{language === 'zh' ? '先行者身份认证，专属社区访问权' : 'Pioneer status badge and exclusive community access'}</div>
                  </div>
                </div>
                
                {work.pioneersCount && (
                  <div className="mt-4 bg-white bg-opacity-60 p-3 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{language === 'zh' ? '当前先行者数量' : 'Current Pioneer Count'}:</span>
                      <span className="font-bold text-purple-600">{work.pioneersCount} {language === 'zh' ? '位' : 'pioneers'}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Description & Tags */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{language === 'zh' ? '作品介绍' : 'Work Introduction'}</h3>
              <p className="text-gray-600 leading-relaxed mb-4">{work.description}</p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {work.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className={`px-3 py-1 text-sm rounded-full font-medium ${
                      tag === 'Demo' 
                        ? 'bg-orange-50 text-orange-600'
                        : 'bg-blue-50 text-blue-600'
                    }`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              
              <div className="text-sm text-gray-500">
                {language === 'zh' ? '发布时间' : 'Release Date'}：{work.releaseDate} • {language === 'zh' ? '播放次数' : 'Play Count'}：{work.plays.toLocaleString()}
              </div>
            </div>

            {/* Comments Section */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">{language === 'zh' ? '评论区' : 'Comments'} ({work.comments})</h3>
              
              {/* Add Comment */}
              <div className="mb-6">
                <div className="flex space-x-3">
                  <img 
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
                    alt="Your avatar"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <textarea
                      placeholder={language === 'zh' ? '写下你的评论...' : 'Write your comment...'}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      rows={3}
                    />
                    <div className="mt-3 flex justify-end">
                      <button 
                        onClick={handleSubmitComment}
                        disabled={!newComment.trim()}
                        className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        {language === 'zh' ? '发布' : 'Post'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments List */}
              <div className="space-y-6">
                {work.commentsList.map((comment) => (
                  <div key={comment.id} className="flex space-x-3">
                    <img 
                      src={comment.user.avatar}
                      alt={comment.user.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-medium text-gray-900">{comment.user.name}</span>
                          <span className="text-sm text-gray-500">{comment.timestamp}</span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{comment.content}</p>
                      </div>
                      <div className="mt-2 flex items-center space-x-4">
                        <button 
                          onClick={() => handleCommentLike(comment.id)}
                          className={`flex items-center space-x-1 text-sm transition-colors ${
                            comment.liked ? 'text-red-600' : 'text-gray-500 hover:text-red-600'
                          }`}
                        >
                          {comment.liked ? (
                            <HeartIconSolid className="h-4 w-4" />
                          ) : (
                            <HeartIcon className="h-4 w-4" />
                          )}
                          <span>{comment.likes}</span>
                        </button>
                        <button className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                          {language === 'zh' ? '回复' : 'Reply'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Copyright Info */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{language === 'zh' ? '版权信息' : 'Copyright Information'}</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">{language === 'zh' ? '作曲' : 'Composer'}</span>
                  <span className="text-gray-900 font-medium">{work.copyrightInfo.composer}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{language === 'zh' ? '作词' : 'Lyricist'}</span>
                  <span className="text-gray-900 font-medium">{work.copyrightInfo.lyricist}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{language === 'zh' ? '制作人' : 'Producer'}</span>
                  <span className="text-gray-900 font-medium">{work.copyrightInfo.producer}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{language === 'zh' ? '发行方' : 'Label'}</span>
                  <span className="text-gray-900 font-medium">{work.copyrightInfo.label}</span>
                </div>
              </div>
            </div>

            {/* NFT Information */}
            {(() => {
              const publishedWork = publishedWorks.find(w => 
                w.id.toString() === id || 
                (typeof w.id === 'string' && w.id === id)
              );
              if (publishedWork?.nft) {
                return (
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl shadow-lg p-6 border border-purple-200">
                    <div className="flex items-center gap-3 mb-4">
                      <CubeTransparentIcon className="w-6 h-6 text-purple-600" />
                      <h3 className="text-lg font-bold text-gray-900">{language === 'zh' ? 'NFT数字收藏品' : 'NFT Digital Collectible'}</h3>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                        {language === 'zh' ? '限量版' : 'Limited Edition'}
                      </span>
                    </div>
                    
                    <div className="space-y-3 text-sm mb-6">
                      <div className="flex justify-between">
                        <span className="text-gray-500">{language === 'zh' ? 'NFT编号' : 'NFT ID'}</span>
                        <span className="text-gray-900 font-mono font-medium">{publishedWork.nft.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">{language === 'zh' ? 'Token ID' : 'Token ID'}</span>
                        <span className="text-gray-900 font-mono font-medium">{publishedWork.nft.tokenId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">{language === 'zh' ? '价格' : 'Price'}</span>
                        <span className="text-gray-900 font-medium">{publishedWork.nft.price} {publishedWork.nft.currency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">{language === 'zh' ? '发行时间' : 'Release Time'}</span>
                        <span className="text-gray-900 font-medium">
                          {new Date(publishedWork.nft.createdAt).toLocaleDateString('zh-CN')}
                        </span>
                      </div>
                    </div>
                    
                    {publishedWork.nft.isForSale && (
                      <button 
                        onClick={() => {
                          alert(`${language === 'zh' ? 'NFT购买提示' : 'NFT Purchase Alert'}\n\n${language === 'zh' ? '作品' : 'Work'}: ${work?.title}\n${language === 'zh' ? 'NFT编号' : 'NFT ID'}: ${publishedWork.nft?.id}\n${language === 'zh' ? '购买价格' : 'Purchase Price'}: ${publishedWork.nft?.price} ${publishedWork.nft?.currency}\n\n${language === 'zh' ? '功能开发中，敬请期待！' : 'Feature coming soon!'}`);
                        }}
                        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-200 font-medium"
                      >
                        <CurrencyDollarIcon className="w-5 h-5" />
                        {language === 'zh' ? '立即购买' : 'Buy Now'} ({publishedWork.nft.price} {publishedWork.nft.currency})
                      </button>
                    )}
                  </div>
                );
              }
              return null;
            })()}

            {/* Related Works */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{language === 'zh' ? '相关作品' : 'Related Works'}</h3>
              <div className="space-y-4">
                {work.relatedWorks.map((relatedWork) => (
                  <div 
                    key={relatedWork.id} 
                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/works/${relatedWork.id}`)}
                  >
                    <img 
                      src={relatedWork.coverUrl}
                      alt={relatedWork.title}
                      className="w-12 h-12 rounded object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">{relatedWork.title}</h4>
                      <p className="text-xs text-gray-500 truncate">{relatedWork.artist}</p>
                      <div className="flex items-center space-x-2 text-xs text-gray-400 mt-1">
                        <span>{relatedWork.duration}</span>
                        <span>•</span>
                        <span>{(relatedWork.plays / 1000).toFixed(1)}{language === 'zh' ? 'k播放' : 'k plays'}</span>
                      </div>
                    </div>
                    <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                      <PlayIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      {work && (
        <PurchaseModal 
          isOpen={purchaseModal}
          onClose={() => setPurchaseModal(false)}
          work={work}
          language={language}
        />
      )}
    </div>
  );
};

export default WorkDetail;